import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessage } from '@/common/components/ErrorMessage'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { FileUploader } from '@/common/components/FileUploader'
import GoBackButton from '@/common/components/GoBackButton'
import MaskedTextField from '@/common/components/MaskedTextField'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import TextField from '@/common/components/TextField'
import { numberMask } from '@/common/constants/masks.constant'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import { getFilenameFromUrl } from '@/common/utils/getFilenameFromUrl'
import { useCreateEstablishmentRequiredDocument } from '@/features/maintenance/establishmentRequiredDocuments/hooks/useCreateEstablishmentRequiredDocument'
import {
  establishmentRequiredDocumentCreateSchema,
  EstablishmentRequiredDocumentValidationSchema,
} from '@/features/maintenance/establishmentRequiredDocuments/schemas/create.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { QueryClient } from '@tanstack/react-query'
import { ChevronsRightIcon, InfoIcon } from 'lucide-react'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

const Create = () => {
  const goBack = useGoBack()
  const navigate = useNavigate()
  const queryClient = new QueryClient()

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToCreateEstablishmentRequiredDocument =
    isUserPermissionsPending || hasUserPermission('Admin::Maintenance::EstablishmentRequiredDocuments::Create')

  const {
    register,
    setValue,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<EstablishmentRequiredDocumentValidationSchema>({
    resolver: zodResolver(establishmentRequiredDocumentCreateSchema),
    defaultValues: {
      has_default_validity_in_days: false,
    },
  })

  const watchRequiresValidityInDays = watch('has_default_validity_in_days')
  const watchFileSample = watch('file_sample')

  const { mutate: createEstablishmentRequiredDocument, isPending: createEstablishmentRequiredDocumentIsPending } =
    useCreateEstablishmentRequiredDocument({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['establishmentRequiredDocuments'] })
        navigate('/establishment-required-documents')
      },
      onError: FormTransformers.errorsTransformer({ setError, setMessages }),
    })

  const onSubmit: SubmitHandler<EstablishmentRequiredDocumentValidationSchema> = (form) =>
    createEstablishmentRequiredDocument({ form })

  return (
    <ErrorMessageWrapper
      className="h-[calc(100vh-128px)]"
      statusCode={userHasPermissionToCreateEstablishmentRequiredDocument ? 200 : 403}
    >
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="false" className="flex flex-col gap-y-6">
        <div className="flex justify-between items-center">
          <div className="flex gap-2 items-center">
            <GoBackButton fallback="/establishment-required-documents" />

            <p className="text-white-400 font-normal">Documentos Obrigatórios</p>

            <ChevronsRightIcon className="text-white-400" />

            <h2>Novo Documento</h2>
          </div>

          <div className="flex gap-4">
            <Button variant="outline-error" onClick={() => goBack({ fallback: '/establishment-required-documents' })}>
              Cancelar
            </Button>

            <Button
              type="submit"
              name="create_establishment_required_document"
              className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
              disabled={createEstablishmentRequiredDocumentIsPending}
              isLoading={createEstablishmentRequiredDocumentIsPending}
            >
              Criar Documento
            </Button>
          </div>
        </div>

        <Box className="max-h-[calc(100vh-128px)] flex flex-col gap-4 overflow-y-auto">
          <p className="text-lg font-bold">Documentos Obrigatórios</p>

          <div className="flex items-start gap-x-4">
            <div className="w-[11rem]">
              <FileUploader
                title="Anexe um exemplo do documento"
                titleClassName="text-center font-bold"
                className="w-[11rem] h-auto min-h-[100px] bg-white-100 py-4 border-none"
                name="file_sample"
                data-testid="file_sample_input"
                accept="application/pdf"
                handleChange={(file) => {
                  clearErrors('file_sample')
                  setValue('file_sample', file)
                }}
                setError={(message) => setError('file_sample', { message })}
                fileName={
                  !!watchFileSample
                    ? typeof watchFileSample === 'string'
                      ? getFilenameFromUrl(watchFileSample)
                      : watchFileSample?.name
                    : undefined
                }
                currentFileUrl={
                  !!watchFileSample
                    ? typeof watchFileSample === 'string'
                      ? watchFileSample
                      : URL.createObjectURL(watchFileSample)
                    : undefined
                }
              />

              {errors.file_sample?.message && <ErrorMessage name="file_sample" message={errors.file_sample?.message} />}
            </div>

            <div className="flex-1 flex flex-col gap-y-4">
              <TextField
                label="Nome do documento"
                markAsRequired
                {...register('display_name')}
                errorMessage={errors?.display_name?.message}
                disabled={createEstablishmentRequiredDocumentIsPending}
              />

              <div className="flex flex-col gap-y-1">
                <p className="text-xs">Possui validade padrão?</p>

                <div className="flex items-start gap-x-4">
                  <Checkbox
                    name="has_default_validity_in_days"
                    type="radio"
                    className="items-center gap-3"
                    boxClassName="rounded-full size-6"
                    labelClassName="-ml-1.5"
                    label="Sim"
                    onChange={() => setValue('has_default_validity_in_days', true)}
                    disabled={createEstablishmentRequiredDocumentIsPending}
                    dataTestidPrefix="has_default_validity_in_days_true"
                  />

                  <Checkbox
                    name="has_default_validity_in_days"
                    type="radio"
                    className="items-center gap-3"
                    boxClassName="rounded-full size-6"
                    defaultChecked
                    labelClassName="-ml-1.5"
                    label="Não (Não será cobrado validade junto ao documento)"
                    onChange={() => setValue('has_default_validity_in_days', false)}
                    disabled={createEstablishmentRequiredDocumentIsPending}
                    dataTestidPrefix="has_default_validity_in_days_false"
                  />
                </div>
              </div>

              {!!watchRequiresValidityInDays && (
                <>
                  <MaskedTextField
                    label="Validade padrão em dias"
                    markAsRequired
                    mask={numberMask(999_999_999)}
                    {...register('default_validity_in_days')}
                    errorMessage={errors?.default_validity_in_days?.message}
                    disabled={createEstablishmentRequiredDocumentIsPending}
                  />

                  <div className="flex items-center gap-x-2">
                    <InfoIcon strokeWidth={1} className="size-6 text-warning" />

                    <p className="text-xs font-bold">
                      Contabilizaremos a data de emissão e a validade final para validar os documentos enviados por
                      estabelecimentos
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>
        </Box>

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default Create
